// RRStandingsTable — per-pool standings (admin + public). Consumes the
// backend standings source of truth: W · L · GW · GL · Game % (no Played).
import React from 'react';
import MemberLink from '../MemberLink';

function fmtPct(v) { return v == null ? '—' : `${Number(v).toFixed(1)}%`; }

function TeamCell({ entry, fallback }) {
  if (!entry) return <span>{fallback || '—'}</span>;
  if (entry.team_name) return <span className="font-medium">{entry.team_name}</span>;
  const p2 = entry.player2_profile_id || entry.player2_name;
  return (
    <div className="leading-tight">
      <div><MemberLink profileId={entry.player1_profile_id} name={entry.player1_name || entry.display_name} /></div>
      {p2 && <div><MemberLink profileId={entry.player2_profile_id} name={entry.player2_name} /></div>}
    </div>
  );
}

export default function RRStandingsTable({ rr }) {
  const standings = rr.standings || {};
  const labels = rr.pool_labels || {};
  const poolLabel = (pn) => labels[String(pn)] || `Pool ${pn}`;
  const pools = Object.keys(standings).sort((a, b) => Number(a) - Number(b));
  if (pools.length === 0) return null;
  const entriesById = Object.fromEntries((rr.entries || []).map((e) => [e.id, e]));
  return (
    <div className="space-y-2" data-testid="rr-standings">
      <div className="text-[10px] text-[#9aa39d]">Ranked by <span className="font-semibold text-[#6a766e]">Match Wins</span>, then <span className="font-semibold text-[#6a766e]">Game Win %</span>. Exact ties are flagged.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pools.map((p) => (
          <div key={p} className="border border-[#e3dcd1] rounded-2xl bg-white p-4" data-testid={`rr-pool-standings-${p}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-[#013523]">{poolLabel(p)}</div>
              <div className="text-[10px] text-[#9aa39d] uppercase tracking-wider">Standings</div>
            </div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-[#9aa39d]">
                <tr>
                  <th className="text-left py-1 w-8">#</th>
                  <th className="text-left">Team</th>
                  <th className="text-center w-8">W</th>
                  <th className="text-center w-8">L</th>
                  <th className="text-center w-9">GW</th>
                  <th className="text-center w-9">GL</th>
                  <th className="text-center w-14">Game %</th>
                </tr>
              </thead>
              <tbody>
                {standings[p].map((row) => {
                  const entry = entriesById[row.entry_id];
                  return (
                    <tr key={row.entry_id} className={`${row.rank === 1 ? 'font-semibold text-[#013523]' : 'text-[#2a332c]'} border-t border-[#f0ebe1] align-middle`} data-testid={`rr-standings-row-${row.entry_id}`}>
                      <td className="py-1.5 tabular-nums">
                        {row.rank}{row.tie_unresolved && <span className="ml-0.5 text-[9px] text-amber-700" title="Tied on Match Wins and Game Win %">T</span>}
                      </td>
                      <td className="max-w-[220px] py-1"><TeamCell entry={entry} fallback={row.display_name} /></td>
                      <td className="text-center tabular-nums">{row.wins}</td>
                      <td className="text-center tabular-nums">{row.losses}</td>
                      <td className="text-center tabular-nums" data-testid={`rr-standings-row-${row.entry_id}-gw`}>{row.games_won ?? 0}</td>
                      <td className="text-center tabular-nums" data-testid={`rr-standings-row-${row.entry_id}-gl`}>{row.games_lost ?? 0}</td>
                      <td className="text-center tabular-nums" data-testid={`rr-standings-row-${row.entry_id}-pct`}>{fmtPct(row.game_win_pct)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
